import React, { useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { AcaoConfirmarPdf, IAuditFile, StatusArquivo } from '../interfaces/IAuditoriaEntradaDados';
import AuditProcessingProgress from './AuditProcessingProgress';
import AuditMessage from './AuditMessage';
import AuditPdfPanel from './AuditPdfPanel';

interface Props {
  arquivo: IAuditFile;
  onVerHistorico: (arquivo: IAuditFile) => void;
  onAdicionarNovo: () => void;
  onSubstituir: (arquivo: IAuditFile) => void;
  onReprocessar: (arquivo: IAuditFile) => void;
  onExcluir: (arquivo: IAuditFile) => void;
  onVerResultado?: (arquivo: IAuditFile) => void;
  onDecidirPdf?: (arquivo: IAuditFile, processar: boolean) => void;
  onConfirmarPdf?: (arquivo: IAuditFile, acao: AcaoConfirmarPdf) => void;
  reprocessando: boolean;
  // Uma ação sobre este arquivo está a caminho do backend: trava os botões do PDF.
  ocupado?: boolean;
}

const STATUS_ARQUIVO_BADGE: Record<StatusArquivo, { label: string; cls: string }> = {
  PENDING_UPLOAD: { label: 'Aguardando envio', cls: 'badge-light-secondary' },
  UPLOADING: { label: 'Enviando', cls: 'badge-light-primary' },
  UPLOADED: { label: 'Enviado', cls: 'badge-light-primary' },
  VALIDATING: { label: 'Validando', cls: 'badge-light-primary' },
  VALIDATION_WARNING: { label: 'Validado c/ aviso', cls: 'badge-light-warning' },
  VALIDATION_ERROR: { label: 'Erro de validação', cls: 'badge-light-danger' },
  READY_TO_PROCESS: { label: 'Pronto p/ processar', cls: 'badge-light-info' },
  PROCESSING: { label: 'Processando', cls: 'badge-light-primary' },
  PROCESSED: { label: 'Processado', cls: 'badge-light-success' },
  REPROCESSING: { label: 'Reprocessando', cls: 'badge-light-primary' },
  PROCESSING_ERROR: { label: 'Erro no processamento', cls: 'badge-light-danger' },
  AWAITING_COST_DECISION: { label: 'Aguardando decisão', cls: 'badge-light-warning' },
  AWAITING_CONFIRMATION: { label: 'Aguardando confirmação', cls: 'badge-light-warning' },
  CANCELLED: { label: 'Cancelado', cls: 'badge-light-secondary' },
  REPLACED: { label: 'Substituído', cls: 'badge-light-secondary' },
};

const VALIDACAO_BADGE: Record<string, { label: string; cls: string }> = {
  OK: { label: 'OK', cls: 'badge-light-success' },
  WARNING: { label: 'Aviso', cls: 'badge-light-warning' },
  ERROR: { label: 'Erro', cls: 'badge-light-danger' },
};

const formatarTamanho = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const formatarData = (iso: string) => new Date(iso).toLocaleString('pt-BR');
const formatarPeriodo = (inicio?: string, fim?: string) => {
  if (!inicio || !fim) return '—';
  const f = (iso: string) => new Date(iso).toLocaleDateString('pt-BR');
  return `${f(inicio)} a ${f(fim)}`;
};

// Épico "Central de Entrada de Dados", seção 42 — linha da listagem de arquivos. Ações
// (seção 40/41 do Épico de Mensagens) respeitam SEMPRE `arquivo.permissions`, vindo do backend —
// nunca decididas localmente (CA24/CA42).
const AuditFileItem: React.FC<Props> = ({
  arquivo,
  onVerHistorico,
  onAdicionarNovo,
  onSubstituir,
  onReprocessar,
  onExcluir,
  onVerResultado,
  onDecidirPdf,
  onConfirmarPdf,
  reprocessando,
  ocupado,
}) => {
  const [menuAberto, setMenuAberto] = useState(false);
  const [menuPos, setMenuPos] = useState<{ top: number; right: number } | null>(null);
  const [mostrarValidacoes, setMostrarValidacoes] = useState(false);
  const botaoAcoesRef = useRef<HTMLButtonElement>(null);
  const statusInfo = STATUS_ARQUIVO_BADGE[arquivo.status];
  const validacoesComAtencao = arquivo.validations.filter((v) => v.status !== 'OK');
  const aguardandoUsuario = arquivo.status === 'AWAITING_COST_DECISION' || arquivo.status === 'AWAITING_CONFIRMATION';
  const podeVerResultado = !!onVerResultado && !!arquivo.permissions.canViewResult;

  // O menu era renderizado dentro da célula da tabela e ficava cortado pelo `.table-responsive`
  // (overflow-x: auto força overflow-y a clipar conteúdo posicionado em absolute) — por isso
  // aparecia "flutuando" sem o card/sombra visíveis. Com um portal para o body, posicionado a
  // partir do botão, o menu sempre renderiza por cima da tabela, sem recortes.
  // ALTURA_ESTIMADA cobre os 6 itens do menu — em linhas perto do fim da janela, abre para CIMA
  // do botão em vez de para baixo, senão o menu estoura a viewport (fica cortado sem dar pra
  // rolar, por estar em position:fixed).
  const ALTURA_ESTIMADA_MENU = 250;
  const alternarMenu = () => {
    if (menuAberto) {
      setMenuAberto(false);
      return;
    }
    const rect = botaoAcoesRef.current?.getBoundingClientRect();
    if (rect) {
      const espacoAbaixo = window.innerHeight - rect.bottom;
      const top =
        espacoAbaixo >= ALTURA_ESTIMADA_MENU
          ? rect.bottom + 4
          : Math.max(8, rect.top - ALTURA_ESTIMADA_MENU - 4);
      setMenuPos({ top, right: window.innerWidth - rect.right });
    }
    setMenuAberto(true);
  };

  return (
    <>
    <tr>
      <td>
        <div className='fw-bold'>{arquivo.originalFileName}</div>
        <div className='text-muted fs-9'>
          v{arquivo.versionNumber}
          {arquivo.fileHashSha256 ? ` · ${arquivo.fileHashSha256.slice(0, 12)}…` : ''}
        </div>
      </td>
      <td>{arquivo.extension.toUpperCase()}</td>
      <td>{arquivo.accountingAccountLabel || '—'}</td>
      <td>{formatarPeriodo(arquivo.periodStart, arquivo.periodEnd)}</td>
      <td>{formatarTamanho(arquivo.fileSize)}</td>
      <td>
        <div>{formatarData(arquivo.uploadedAt)}</div>
        <div className='text-muted fs-9'>{arquivo.uploadedByUserName}</div>
      </td>
      <td className='text-center'>
        <span className={`badge ${statusInfo.cls}`}>{statusInfo.label}</span>
      </td>
      <td className='text-center'>
        {arquivo.validationStatus ? (
          <button
            type='button'
            className={`badge ${VALIDACAO_BADGE[arquivo.validationStatus].cls} border-0`}
            style={{ cursor: validacoesComAtencao.length > 0 ? 'pointer' : 'default' }}
            onClick={() => validacoesComAtencao.length > 0 && setMostrarValidacoes((v) => !v)}
            title={validacoesComAtencao.length > 0 ? 'Ver detalhes da validação' : undefined}
          >
            {VALIDACAO_BADGE[arquivo.validationStatus].label}
            {validacoesComAtencao.length > 0 && (
              <i className={`bi bi-chevron-${mostrarValidacoes ? 'up' : 'down'} ms-1`} />
            )}
          </button>
        ) : (
          '—'
        )}
      </td>
      <td style={{ minWidth: 220 }}>
        {aguardandoUsuario ? (
          <span className='text-muted'>{arquivo.processing?.statusMessage || 'Aguardando a sua decisão.'}</span>
        ) : (
          <AuditProcessingProgress
            processing={arquivo.processing ?? undefined}
            onReprocessar={arquivo.permissions.canReprocess ? () => onReprocessar(arquivo) : undefined}
            onVerResultado={podeVerResultado ? () => onVerResultado!(arquivo) : undefined}
            reprocessando={reprocessando}
          />
        )}
        {!arquivo.processing && !aguardandoUsuario && <span className='text-muted'>—</span>}
      </td>
      <td className='text-end'>
        <div className='audit-actions-menu-wrap'>
          <button
            ref={botaoAcoesRef}
            type='button'
            className='btn btn-sm btn-icon btn-light'
            title='Ações'
            onClick={alternarMenu}
          >
            <i className='bi bi-three-dots-vertical' />
          </button>
          {menuAberto && menuPos && createPortal(
            <>
              <div className='audit-actions-menu-backdrop' onClick={() => setMenuAberto(false)} />
              <div
                className='audit-actions-menu'
                style={{ position: 'fixed', top: menuPos.top, right: menuPos.right }}
              >
                <button
                  type='button'
                  disabled={!podeVerResultado}
                  onClick={() => { setMenuAberto(false); onVerResultado?.(arquivo); }}
                >
                  <i className='bi bi-bar-chart-line me-2' /> Ver resultado
                </button>
                <button type='button' onClick={() => { setMenuAberto(false); onVerHistorico(arquivo); }}>
                  <i className='bi bi-clock-history me-2' /> Ver histórico
                </button>
                <button type='button' onClick={() => { setMenuAberto(false); onAdicionarNovo(); }}>
                  <i className='bi bi-file-earmark-plus me-2' /> Adicionar novo arquivo
                </button>
                <button
                  type='button'
                  disabled={!arquivo.permissions.canReplace}
                  onClick={() => { setMenuAberto(false); onSubstituir(arquivo); }}
                >
                  <i className='bi bi-arrow-repeat me-2' /> Substituir arquivo
                </button>
                <button
                  type='button'
                  disabled={!arquivo.permissions.canReprocess}
                  onClick={() => { setMenuAberto(false); onReprocessar(arquivo); }}
                >
                  <i className='bi bi-arrow-clockwise me-2' /> Reprocessar
                </button>
                <button
                  type='button'
                  className='text-danger'
                  disabled={!arquivo.permissions.canDelete}
                  onClick={() => { setMenuAberto(false); onExcluir(arquivo); }}
                >
                  <i className='bi bi-trash me-2' /> Excluir arquivo
                </button>
              </div>
            </>,
            document.body
          )}
        </div>
      </td>
    </tr>
    {aguardandoUsuario && onDecidirPdf && onConfirmarPdf && (
      <tr>
        <td colSpan={10} className='bg-light'>
          <AuditPdfPanel arquivo={arquivo} ocupado={ocupado} onDecidir={onDecidirPdf} onConfirmar={onConfirmarPdf} />
        </td>
      </tr>
    )}
    {mostrarValidacoes && validacoesComAtencao.length > 0 && (
      <tr>
        <td colSpan={10} className='bg-light'>
          <div className='p-3 d-flex flex-column gap-2'>
            {validacoesComAtencao.map((v) => (
              <AuditMessage
                key={v.id}
                type={v.validationType}
                title={v.message}
                message={
                  v.expectedValue || v.detectedValue
                    ? `Selecionado: ${v.expectedValue ?? '—'} · Identificado no arquivo: ${v.detectedValue ?? '—'}`
                    : undefined
                }
              />
            ))}
          </div>
        </td>
      </tr>
    )}
    </>
  );
};

export default AuditFileItem;
